import PortalItem from '@arcgis/core/portal/PortalItem';
import IdentityManager from '@arcgis/core/identity/IdentityManager';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Layer from '@arcgis/core/layers/Layer';
import { oauthPortal } from './arcGisPortal';

export type ArcGisAuthOptions =
  | { accessType: 'apiKey'; apiKey: string }
  | { accessType: 'userAuth'; portalUrl: string }
  | { accessType: 'public' };

/**
 * Portal FeatureLayers often ship with a restricted outFields list from the
 * item popup. Force '*' so all service attributes (e.g. priority_score) load.
 */
export function ensureFeatureLayerOutFields(layer: __esri.Layer): void {
  if (layer instanceof GroupLayer) {
    layer.layers.forEach((child) => ensureFeatureLayerOutFields(child));
    return;
  }
  if (layer instanceof FeatureLayer) {
    layer.outFields = ['*'];
  }
}
export async function createPortalItem({
  portalItemId,
  options,
}: {
  portalItemId: string;
  options: ArcGisAuthOptions;
}) {
  if (options.accessType === 'apiKey') {
    const portalItem = new PortalItem({
      id: portalItemId,
      apiKey: options.apiKey,
    });

    await portalItem.load();

    if (portalItem.url) {
      IdentityManager.registerToken({
        server: portalItem.url,
        token: options.apiKey,
      });
    }

    return portalItem;
  }

  if (options.accessType === 'public') {
    const portalItem = new PortalItem({ id: portalItemId });
    await portalItem.load();
    return portalItem;
  }

  return new PortalItem({
    id: portalItemId,
    portal: oauthPortal({
      authMode: 'immediate',
      url: options.portalUrl,
    }),
  });
}

/** GeoJSON feeds (e.g. the USGS earthquake feed) are not ArcGIS services. */
export function isGeoJsonServiceUrl(serviceUrl: string): boolean {
  return /\.geojson(\?|$)/i.test(serviceUrl.trim());
}

export async function createServiceUrlLayer({
  serviceUrl,
  options,
}: {
  serviceUrl: string;
  options: ArcGisAuthOptions;
}) {
  if (isGeoJsonServiceUrl(serviceUrl)) {
    const layer = new GeoJSONLayer({ url: serviceUrl });
    await layer.load();
    layer.outFields = ['*'];

    return layer;
  }

  if (options.accessType === 'apiKey') {
    IdentityManager.registerToken({
      server: serviceUrl,
      token: options.apiKey,
    });
  }

  const layer = await Layer.fromArcGISServerUrl({ url: serviceUrl });
  await layer.load();
  ensureFeatureLayerOutFields(layer);

  return layer;
}
